import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import {
  checkAnalyticsConsent,
  getAuthenticatedUserToken,
  getGoogleAnalyticsUserToken,
  getSearchCallUserToken,
} from '@/lib/algoliaUserToken';
import { userState } from '@/states/user';

export interface UseAlgoliaUserTokenReturn {
  /**
   * Authenticated user token for Algolia analytics events.
   * Only set when user is authenticated. Format: `user_<userId>`
   * Per BEC-20960: Use this for analytics events when user is logged in.
   */
  authenticatedUserToken: string | undefined;
  /** Whether the Google Analytics cookie (_ga) is available */
  hasGoogleAnalytics: boolean;
  isLoading: boolean;
  /**
   * Whether the user state has been initialized.
   * Use this to determine when it's safe to switch from SSR token to client token.
   * Per BEC-20960: Prevents duplicate Algolia requests with different tokens.
   */
  isUserInitialized: boolean;
  refreshToken: () => void;
  /**
   * User token for Algolia search API calls.
   *
   * Per BEC-20960, format depends on auth state:
   * - Authenticated: `user_<userId>`
   * - Anonymous + consented: `anonymous_consented_<ga_id>`
   * - Anonymous + consented but no GA yet: `anonymous_consented_<uuid>`
   * - Anonymous + no consent: `anonymous_<uuid>`
   */
  userToken: string | undefined;
}

/**
 * Returns the user token for Algolia analytics/personalization.
 *
 * The token is built dynamically based on:
 * - Stable UUID stored in algolia_user_id cookie
 * - Consent status from OptanonConsent cookie
 * - Google Analytics ID (if consented and available)
 */
export const useAlgoliaUserToken = (): UseAlgoliaUserTokenReturn => {
  const userSnapshot = useSnapshot(userState);

  // Track GA availability changes via state (for consent/cookie updates)
  const [gaUpdateTrigger, setGaUpdateTrigger] = useState(0);

  // BEC-20960: For search calls, reads auth state from cookies directly.
  // Authenticated → `user_{userId}`, anonymous → `anonymous_consented_{gaId}` or `anonymous_{uuid}`.
  // Dependencies include auth snapshot for reactivity when user logs in/out.
  const userToken = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    return getSearchCallUserToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaUpdateTrigger, userSnapshot.isAuthenticated, userSnapshot.user?.id]);

  const authenticatedUserToken = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    return getAuthenticatedUserToken(
      userSnapshot.isAuthenticated,
      userSnapshot.user?.id,
    );
  }, [userSnapshot.isAuthenticated, userSnapshot.user?.id]);

  const hasGoogleAnalytics = useMemo(() => {
    if (typeof window === 'undefined') return false;
    // Re-check when gaUpdateTrigger changes
    return !!getGoogleAnalyticsUserToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaUpdateTrigger]);

  const refreshToken = useCallback(() => {
    // Trigger re-computation of memoized values
    setGaUpdateTrigger((prev) => prev + 1);
  }, []);

  // Listen for events that might change the token (consent, storage, etc.)
  useEffect(() => {
    const handleUpdate = () => {
      setTimeout(() => setGaUpdateTrigger((prev) => prev + 1), 100);
    };

    // Register with OneTrust for consent changes
    const registerOneTrustCallback = () => {
      const win = window as typeof window & {
        OneTrust?: { OnConsentChanged?: (callback: () => void) => void };
      };
      if (win.OneTrust?.OnConsentChanged) {
        win.OneTrust.OnConsentChanged(handleUpdate);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleUpdate);
      window.addEventListener('focus', handleUpdate);
      window.addEventListener('OneTrustGroupsUpdated', handleUpdate);

      registerOneTrustCallback();
      setTimeout(registerOneTrustCallback, 2000);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleUpdate);
        window.removeEventListener('focus', handleUpdate);
        window.removeEventListener('OneTrustGroupsUpdated', handleUpdate);
      }
    };
  }, []);

  // Poll for GA cookie when consent is given but GA isn't available yet
  // This ensures we use GA ID instead of UUID once GA loads
  // Uses exponential backoff to reduce unnecessary checks
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hasConsent = checkAnalyticsConsent();
    const hasGA = !!getGoogleAnalyticsUserToken();

    // Only poll if consent is given but GA cookie is missing
    if (!hasConsent || hasGA) return;

    const maxPollTime = 10000; // 10 seconds max
    const initialInterval = 100; // Start checking quickly
    const maxInterval = 2000; // Cap at 2 seconds between checks
    const backoffMultiplier = 1.5;

    let currentInterval = initialInterval;
    const startTime = Date.now();
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = () => {
      const now = Date.now();
      const gaToken = getGoogleAnalyticsUserToken();

      // Stop polling if GA cookie is found or max time exceeded
      if (gaToken) {
        // GA cookie found - trigger update
        setGaUpdateTrigger((prev) => prev + 1);
        return;
      }

      if (now - startTime >= maxPollTime) {
        // Max time exceeded, stop polling
        return;
      }

      // Schedule next poll with exponential backoff
      currentInterval = Math.min(
        currentInterval * backoffMultiplier,
        maxInterval,
      );
      timeoutId = setTimeout(poll, currentInterval);
    };

    // Start polling
    timeoutId = setTimeout(poll, initialInterval);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [hasGoogleAnalytics]);

  return {
    authenticatedUserToken,
    hasGoogleAnalytics,
    isLoading: false,
    isUserInitialized: userSnapshot.isInitialized,
    refreshToken,
    userToken,
  };
};
